/*
 * Copyright (C) 2018 - 2023 Entgra (Pvt) Ltd, Inc - All Rights Reserved.
 *
 * Unauthorised copying/redistribution of this file, via any medium is strictly prohibited.
 *
 * Licensed under the Entgra Commercial License, Version 1.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *       https://entgra.io/licenses/entgra-commercial/1.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import React from 'react';
import { withTranslation } from 'react-i18next';
import { Button, Card, Col, Row, Select, Tree } from 'antd';
import axios from 'axios';
import { DownOutlined } from '@ant-design/icons';
import { isAuthorized } from '../../../../../../../services/utils/authorizationHandler';
import { handleApiError } from '../../../../../../../services/utils/errorHandler';
import { withConfigContext } from '../../../../../../../components/ConfigContext';
import GroupTree from '../../GroupTree';
import { Link } from 'react-router-dom';
import styles from './styles.module.css';
// eslint-disable-next-line no-empty-pattern
const {} = Select;
const { TreeNode } = Tree;

class TreeHierarchy extends React.Component {
  constructor(props) {
    super(props);
    this.config = this.props.context;
    this.selected = [];
    this.expandKeys = [];
    this.state = {
      childrenGroups: [],
      isNodeList: false,
      expandedKeys: [],
      autoExpandParent: true,
      checkedKeys: [],
      hierarchy: true,
      selectedGroups: [],
      selectedKeys: [],
      group: [],
      isButtonClicked: false,
    };
  }

  handleButtonClick = () => {
    // Call the callback function passed as a prop with the necessary data
    if (this.props.onButtonClick) {
      this.props.onButtonClick(this.state.group);

      this.setState({ isButtonClicked: true });
    }
  };

  renderTreeNodes = data => {
    const { t } = this.props;
    return data.map(item => {
      if (item !== null) {
        if (item.hasOwnProperty('childrenGroups')) {
          return (
            <TreeNode
              title={t(`${item.name.split('-').join('_')}`)}
              key={item.resourcePath}
              dataRef={item}
            >
              {this.renderTreeNodes(item.childrenGroups)}
            </TreeNode>
          );
        }
        return (
          <TreeNode
            title={t(`${item.name.split('-').join('_')}`)}
            key={item.resourcePath}
            {...item}
          />
        );
      }

      // eslint-disable-next-line react/jsx-key
      return <TreeNode />;
    });
  };

  onExpand = expandedKeys => {
    this.setState({
      expandedKeys,
      autoExpandParent: false,
    });
  };

  onCheck = (checkedKeys, info) => {
    this.setState({ isButtonClicked: false });
    if (info.node.dataRef) {
      this.setState({
        checkedKeys: checkedKeys,
        group: info.node.dataRef,
      });
    } else {
      this.setState({
        checkedKeys: checkedKeys,
        group: info.node,
      });
    }
  };
  componentDidMount() {
    this.getGroupsList();
  }

  getCheckedGroups = data => {
    data.forEach(item => {
      if (item !== null) {
        if (item.parentPath) {
          this.expandKeys.push(item?.parentPath);
        }
        if (item.childrenGroups) {
          this.getCheckedGroups(item?.childrenGroups);
        }
      } else {
        return null;
      }
    });
  };

  // fetch data from api
  getGroupsList = () => {
    let apiUrl;
    const { t } = this.props;
    if (isAuthorized(this.config.scopes, ['perm:admin-groups:view'])) {
      apiUrl =
        window.location.origin +
        this.config.serverConfig.invoker.uri +
        this.config.serverConfig.invoker.deviceMgt +
        '/admin/groups/hierarchy';
    } else if (isAuthorized(this.config.scopes, ['perm:groups:groups'])) {
      apiUrl =
        window.location.origin +
        this.config.serverConfig.invoker.uri +
        this.config.serverConfig.invoker.deviceMgt +
        '/groups/hierarchy';
    }
    apiUrl += '?depth=99&offset=0&limit=99';

    // send request to the invokers
    axios
      .get(apiUrl)
      .then(res => {
        if (res.status === 200) {
          this.getCheckedGroups(res.data.data.deviceGroups);
          this.setState({
            childrenGroups: res.data.data.deviceGroups,
            isNodeList: true,
            checkedKeys: this.selected,
            expandedKeys: this.expandKeys,
          });
        }
      })
      .catch(error => {
        handleApiError(
          error,
          t('api_loadError', { label: t('label_deviceGroups') }),
          t,
        );
      });
  };

  render() {
    const { childrenGroups, group, isButtonClicked } = this.state;
    const { t } = this.props;
    const { checkedKeys } = this.state;
    const isButtonDisabled = checkedKeys.length === 0;
    return (
      <div>
        <Card>
          {this.state.hierarchy && (
            <div>
              <div style={{ alignItems: 'center' }}>
                <div>
                  {this.state.isNodeList && (
                    <GroupTree
                      showLine
                      defaultExpandAll
                      switcherIcon={<DownOutlined />}
                      onExpand={this.onExpand}
                      expandedKeys={this.state.expandedKeys}
                      autoExpandParent={this.state.autoExpandParent}
                      onCheck={this.onCheck}
                      checkedKeys={this.state.checkedKeys}
                      data={childrenGroups}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
          <Row>
            <Col lg={{ span: 1, offset: 18 }}>
              <div className={styles.buttons}>
                {isButtonClicked ? (
                  <Link
                    to={{
                      pathname: `/${this.config.appName}/groups/${group.id}`,
                      state: { group },
                    }}
                    key="SubGroups"
                  >
                    <Button
                      type="primary"
                      htmlType="submit"
                      className={styles.btn}
                      disabled={isButtonDisabled}
                    >
                      {t('label_go_to_the_group')}
                    </Button>
                  </Link>
                ) : (
                  <Link
                    to={{
                      pathname: `/${this.config.appName}/groups/${group.id}`,
                      state: { group },
                    }}
                    key="SubGroups"
                  >
                    <Button
                      type="primary"
                      htmlType="submit"
                      className={styles.btn}
                      onClick={this.handleButtonClick}
                      disabled={isButtonDisabled}
                    >
                      {t('label_go_to_the_group')}
                    </Button>
                  </Link>
                )}
              </div>
            </Col>
          </Row>
        </Card>
      </div>
    );
  }
}

export default withConfigContext(withTranslation()(TreeHierarchy));
